/**
 * How far a step's settings are set up, said in words ("2 of 3 set up ·
 * 1 open"), so neither the sidebar card nor the settings pane leaves the user
 * to count marks. Settings that are not needed do not count.
 */
import { useI18n } from '@affine/i18n';
import { useCallback } from 'react';

import type { ReadinessState, ReadinessTileId } from './automation-readiness';

export interface SettingProgress {
  /** Set up. */
  done: number;
  /** Still missing or failing. */
  open: number;
  /** Needed: `done + open`. */
  total: number;
}

export const settingProgress = (
  tiles: readonly ReadinessTileId[],
  states: Readonly<Record<ReadinessTileId, ReadinessState>>
): SettingProgress => {
  let done = 0;
  let open = 0;
  for (const tile of tiles) {
    if (states[tile] === 'ok') done += 1;
    else if (states[tile] !== 'none') open += 1;
  }
  return { done, open, total: done + open };
};

/** "2 of 3 set up · 1 open"; without open settings only "3 of 3 set up". */
export const useSettingProgressLabel = () => {
  const t = useI18n();
  return useCallback(
    ({ done, open, total }: SettingProgress) => {
      const setUp = t.t('com.streamlain.automation.panel.progress', {
        done,
        total,
      });
      return open > 0
        ? `${setUp} · ${t.t('com.streamlain.automation.panel.progress-open', {
            count: open,
          })}`
        : setUp;
    },
    [t]
  );
};
